import { toImmutable } from 'nuclear-js';
import { courses } from '@nwea/subject-course-data';

const SectionIDs = {
    comparisons: 'comparisons',
    instructionalAreas: 'instructional-areas',
    growthGoals: 'growth-goals',
    growthOverTime: 'growth-over-time',
    gotMissingFutureTerms: 'growth-over-time-missing-future-terms',
    header: 'header',
    studentInfo: 'student-info',
    subjectTab: 'subject-tab',
    footer: 'footer',
    studentSelection: 'student-selection',
    highlights: 'highlights',
    printAndShare: 'print-and-share',
};

export const scaleClass = {
    1: './math-icon.svg',
    2: './read-icon.svg',
    3: './lang-icon.svg',
    4: './scie-icon.svg',
    12: './spanish-read-icon.svg',
};

export const scalePrintClass = {
    1: './math-icon-print.svg',
    2: './read-icon-print.svg',
    3: './lang-icon-print.svg',
    4: './scie-icon-print.svg',
    12: './spanish-read-icon-print.svg',
};

export const SINGLE_STUDENT_PATH = '/single-student/profile';

export const WINTER_TERM = 1;
export const SPRING_TERM = 2;
export const FALL_TERM = 4;
export const actionGoalTextCounter = 4000;
export const MAX_STUDENTS_FOR_BATCH_PRINT = 100; // TODO: Change to appropriate number for deployment to PROD

export const QUADRANT_CHART_DEFAULT_ORIENTATION = 'default';
export const QUADRANT_CHART_ROTATED_ORIENTATION = 'rotated';
export const INSTRUCTION_AREAS_GROUPBY_TOPIC = 'topic';
export const INSTRUCTION_AREAS_GROUPBY_STANDARD = 'standard';

export const seasonNames = {
    1: { name: 'Winter', abbreviation: 'Win' },
    2: { name: 'Spring', abbreviation: 'Spr' },
    3: { name: 'Summer', abbreviation: 'Sum' },
    4: { name: 'Fall', abbreviation: 'Fall' },
};

export const growthSummaryProjectionNormCoverage = {
    12: { name: 'Reading (Spanish)', min: 0, max:6 },
    4: { name: 'Science K-12', min: 2, max:10 },
    3: { name: 'Language Usage', min: 2, max:11 },
    1: { name: 'Math K-12', min: 0, max:12},
    2: { name: 'Reading', min: 0, max:12},
    100: { name: 'Algebra 1', min: 0, max:12 },
    101: { name: 'Algebra 2', min: 0, max:12 },
    102: { name: 'Geometry', min: 0, max:12 },
    103: { name: 'Integrated Math 1', min: 0, max:12 },
    105: { name: 'Integrated Math 2', min: 0, max:12 },
    106: { name: 'Integrated Math 3', min: 0, max:12 },
    108: { name: 'Life Sciences', min: 8, max:12 }
};

export const ritQuintilePercentiles = [20, 40, 50, 60, 80];

export const StandardTypes = {
    topic: {
        valueFromEndpoint: 'TOPIC',
        storeValue: INSTRUCTION_AREAS_GROUPBY_TOPIC,
    },
    standard: {
        valueFromEndpoint: 'STANDARD_STATEMENT',
        storeValue: INSTRUCTION_AREAS_GROUPBY_STANDARD,
    },
    descartes: {
        valueFromEndpoint: 'STANDARD_STATEMENT', // they are called standards, but aren't really
        storeValue: 'descartes',
    },
};

const LearningStatementTypes = {
    NONE: {
        StandardTypes: [],
        id: 'none',
    },
    LEARNING_STATEMENTS_TOPIC: {
        StandardTypes: [StandardTypes.topic.storeValue],
        id: 'learning-statements-topic',
    },
    DESCARTES: {
        StandardTypes: [StandardTypes.descartes.storeValue],
        id: 'descartes',
    },
    LEARNING_STATEMENTS: {
        StandardTypes: [StandardTypes.standard.storeValue, StandardTypes.topic.storeValue],
        id: 'learning-statements',
    },
    DESCARTES_AND_LEARNING_STATEMENTS_TOPIC: {
        StandardTypes: [StandardTypes.topic.storeValue],
        id: 'descartes-and-learning-statements-topic',
    },
    PGID: {
        StandardTypes: [StandardTypes.topic.storeValue],
        id: 'pgid',
    },
    DESCARTES_AND_LEARNING_STATEMENTS: {
        StandardTypes: [StandardTypes.standard.storeValue, StandardTypes.topic.storeValue],
        id: 'descartes-and-learning-statements',
    },
    PGID_AND_LEARNING_STATEMENTS: {
        StandardTypes: [StandardTypes.standard.storeValue, StandardTypes.topic.storeValue],
        id: 'pgid-and-learning-statements',
    },
};

export const linkingStudyTypes = {
    stateLinkingStudy: 'STATE',
    collegeLinkingStudy: 'COLLEGE',
    defaultStateLinkingStudy: 'DEFAULT_STATE',
};

export const mostRecentTermNumber = 900001;

export const goalStatuses = {
    met: {
        description: 'GOAL MET',
        className: 'goal-met',
        testedYn: true,
    },
    notmet: {
        description: 'NOT MET',
        className: 'goal-not-met',
        testedYn: true,
    },
    nottested: {
        description: 'NOT TESTED',
        className: 'goal-not-tested',
        testedYn: false,
    },
};

//We really want to get this mapping from the API and not hard code this into our app.
export const nweaGradeNames = {
    13: 'K', // Kindergarten
    14: 'PK', // Pre-Kindergarten
    15: 'OT', // Other
    16: 'KA', // Kindergarten (AM)
    17: 'KP', // Kindergarten (PM)
};
export const SUBJECT_BID_MATHEMATICS = 1;
export const SUBJECT_BID_READING = 2;
export const SUBJECT_BID_READING_SPANISH = 12;
export const OPTIONAL_SUBJECT_BIDS = [12];
export const subjects = toImmutable([
    {
        bid: 1,
        name: 'Mathematics',
        shortName: 'mathematics',
        disciplineName: 'Mathematics',
        description: 'Mathematics',
        highlightsName: 'mathematics',
        order: 1,
    },
    {
        bid: 2,
        name: 'Reading',
        shortName: 'reading',
        disciplineName: 'Reading',
        description: 'Reading',
        highlightsName: 'reading',
        order: 2,
    },
    {
        bid: 12,
        name: 'Reading - Spanish',
        shortName: 'reading-spanish',
        disciplineName: 'Reading - Spanish',
        description: 'Reading - Spanish',
        highlightsName: 'readingSpanish',
        order: 3,
    },
    {
        bid: 3,
        name: 'Language Usage',
        shortName: 'language',
        disciplineName: 'Language Usage',
        description: 'Language Usage',
        highlightsName: 'languageUsage',
        order: 4,
    },
    {
        bid: 4,
        name: 'Science - General Science',
        shortName: 'science',
        disciplineName: 'Science - General Science',
        description: 'Science',
        highlightsName: 'scienceGeneralScience',
        order: 5,
    },
]);
export const getSubjectFieldforBid = (bid, field = 'name') => {
    return subjects.reduce((acc, subject) => {
        return subject.get('bid') === bid ? subject.get(field) : acc;
    }, null);
};

const logoutUrlForEnv = {
    dev: 'https://sandbox-api.nwea.org/cl-support/adult/logout',
    int: 'https://int-api.nwea.org/cl-support/adult/logout',
    perf: 'https://perf-api.nwea.org/cl-support/adult/logout',
    prd: 'https://api.nwea.org/cl-support/adult/logout',
  };

export const getLogoutURL = (url) => {
    if (url.indexOf("orf01")> -1)
        return logoutUrlForEnv.dev;
    else if (url.indexOf("int03") > -1 )
        return logoutUrlForEnv.int;
    else if (url.indexOf("perf") > -1)
        return logoutUrlForEnv.perf;
    else if (url.indexOf("localhost") > -1)
        return logoutUrlForEnv.dev;
    else
    return logoutUrlForEnv.prd;
   };

export const isCourseSpecificMSID = (measurementScaleID) => {
    return subjects.find((subject) => {
        return subject.get('bid') === measurementScaleID;
    })
        ? false
        : true;
};
export const convertMeasurementScaleToSPv2Subject = (measurementScale) => {
    let subjectIndex = 0;
    if (measurementScale) {
        const selectedCourse = courses.find((course) => course.measurementScaleID === measurementScale);

        if (selectedCourse) {
            let subjectBid = selectedCourse.subjectBid;

            // Spv2 deviates from correctly identifying language arts and spanish reading - so fix these
            if (selectedCourse.measurementScaleID === '3') {
                // language usage gets its own tab
                subjectBid = '3';
            } else if (selectedCourse.measurementScaleID === '7') {
                // spanish reading gets its own tab and a spv2 unique internal bid
                subjectBid = '12';
            }

            // Now find the subject index by subjectBid
            const foundIndex = subjects.findIndex((subject) => {
                return `${subject.get('bid')}` === subjectBid;
            });

            if (foundIndex >= 0) {
                // We will seelct this index
                subjectIndex = foundIndex;
            }
        }
    }

    return subjects.get(subjectIndex);
};

export const MIN_RIT = 100;
export const MAX_RIT = 350;
export const MIN_GROWTH = 1;
export const MAX_GROWTH = 50;
export const MIN_STATUS_PERCENTILE = 1;
export const MAX_STATUS_PERCENTILE = 99;
export const MIN_GROWTH_PERCENTILE = 1;
export const MAX_GROWTH_PERCENTILE = 99;
export const NUM_GOT_TERMS = 12;
export const NUM_FAMILY_GOT_TERMS = 5;
export const AY_PLUS_ONE_TERM = 101;
export const AY_PLUS_TWO_TERMS = 102;
export const ONE_AY = 100;
export const MAX_CHART_VALUE = 400;

// These constants represent the difference in term numbers between terms.
// When going from Fall to Winter, we also need to increment the year.

const FALL_TO_WINTER_TERM = 97;
const SPRING_TO_FALL_TERM = 2;
const WINTER_TO_SPRING_TERM = 1;

export const quintileColors = {
    // these are slightly muted for the graph
    // These have been changed from hex to rgba to
    // ensure consistency in modifications.
    1: 'rgba(226,34,38,1)',
    2: 'rgba(246,142,77,1)',
    3: 'rgba(252,207,64,1)',
    4: 'rgba(118,194,108,1)',
    5: 'rgba(75,192,235,1)',
    6: 'rgba(208,210,213,1)', //grey color for non data
};

export const sharedModalReferences = {
    saveDataPrompt: 'saveDataPrompt',
};

export const GrowthOverTimeActionNames = {
    helpCallOut: 'help-call-out',
    toggleAssessments: 'toggle-assessments',
    toggleGoals: 'toggle-goals',
    toggleProjections: 'toggle-projections',
    toggleTypicalScores: 'toggle-typical-scores',
    toggleDistrictMean: 'toggle-district-mean',
    toggleLinkingStudies: 'toggle-linking-studies',
    openPopover: 'open-popover',
    setGoalPopover: 'set-goal-popover',
    highlights: 'highlights',
};

const emptyStudentPlaceholderKey = 'STUDENT_PLACEHOLDER';
export const EMPTY_STUDENT_PLACEHOLDER = {
    key: emptyStudentPlaceholderKey,
    value: toImmutable({
        studentBid: emptyStudentPlaceholderKey,
        loadTime: 0, // want this student evicted first
    }),
};

export const BATCH_PRINT_ERRORS = {
    REQUEST_PARSING: { type: 1, message: 'There was a problem with the request parameters.' },
};

export const getTermPreferenceForGrowthComparison = () => {
    return new Map([
        ['Fall', ['Fall \\d{4} to Fall \\d{4}']],
        ['Winter', ['Fall \\d{4} to Winter \\d{4}', 'Winter \\d{4} to Winter \\d{4}']],
        [
            'Spring',
            ['Fall \\d{4} to Spring \\d{4}', 'Winter \\d{4} to Spring \\d{4}', 'Spring \\d{4} to Spring \\d{4}'],
        ],
    ]);
};

export default {
    convertMeasurementScaleToSPv2Subject,
    scaleClass,
    scalePrintClass,
    seasonNames,
    growthSummaryProjectionNormCoverage,
    ritQuintilePercentiles,
    linkingStudyTypes,
    goalStatuses,
    mostRecentTermNumber,
    MIN_RIT,
    MAX_RIT,
    StandardTypes,
    LearningStatementTypes,
    MIN_GROWTH,
    MAX_GROWTH,
    MIN_STATUS_PERCENTILE,
    MAX_STATUS_PERCENTILE,
    MIN_GROWTH_PERCENTILE,
    MAX_GROWTH_PERCENTILE,
    MAX_CHART_VALUE,
    SectionIDs,
    nweaGradeNames,
    NUM_GOT_TERMS,
    NUM_FAMILY_GOT_TERMS,
    AY_PLUS_ONE_TERM,
    AY_PLUS_TWO_TERMS,
    ONE_AY,
    WINTER_TERM,
    SPRING_TERM,
    FALL_TERM,
    subjects,
    SUBJECT_BID_MATHEMATICS,
    SUBJECT_BID_READING,
    SUBJECT_BID_READING_SPANISH,
    OPTIONAL_SUBJECT_BIDS,
    FALL_TO_WINTER_TERM,
    SPRING_TO_FALL_TERM,
    WINTER_TO_SPRING_TERM,
    quintileColors,
    sharedModalReferences,
    GrowthOverTimeActionNames,
    EMPTY_STUDENT_PLACEHOLDER,
    actionGoalTextCounter,
    BATCH_PRINT_ERRORS,
    MAX_STUDENTS_FOR_BATCH_PRINT,
    getSubjectFieldforBid,
    QUADRANT_CHART_DEFAULT_ORIENTATION,
    QUADRANT_CHART_ROTATED_ORIENTATION,
    INSTRUCTION_AREAS_GROUPBY_TOPIC,
    INSTRUCTION_AREAS_GROUPBY_STANDARD,
    isCourseSpecificMSID,
    getLogoutURL,
    SINGLE_STUDENT_PATH,
    getTermPreferenceForGrowthComparison,
};
